import {
  ActionIcon,
  Box,
  Group,
  Loader,
  Menu,
  Paper,
  Text,
} from '@mantine/core';
import { IconDots, IconEdit, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useDateDisplay } from '@hooks/useDateDisplay';
import {
  getConditionCodeText,
  getClinicalStatusDisplay,
  getClinicalStatus,
  getOnsetDate,
  getNotes,
  ConditionInputItem,
} from '@/utils/condition.utils';
import { LabeledField } from '@/components/LabeledField';
import { StatusBadge } from '@/components/medplum-react/StatusBadge/StatusBadge';

export type ConditionAction = 'edit' | 'remove';

export interface ConditionListItemProps {
  condition: ConditionInputItem;
  onAction: (action: ConditionAction) => void;
  isDeleting?: boolean;
  hideActions?: boolean;
}

export const ConditionListItem = ({
  condition,
  onAction,
  isDeleting = false,
  hideActions = false,
}: ConditionListItemProps) => {
  const { t } = useTranslation(['patient-pages', 'common']);
  const formatDate = useDateDisplay();

  // Use utility functions for safe nested access
  const conditionName =
    getConditionCodeText(condition) ||
    t('examination.conditions.unknownCondition', {
      ns: 'patient-pages',
      defaultValue: 'Unknown Condition',
    });

  // Extract individual fields
  const clinicalStatus = getClinicalStatus(condition);
  const clinicalStatusCode = clinicalStatus?.coding?.[0]?.code;
  const clinicalStatusDisplay = clinicalStatusCode
    ? t(`diagnosis.clinicalStatus.${clinicalStatusCode}`, {
        ns: 'patient-pages',
        defaultValue: getClinicalStatusDisplay(condition) || clinicalStatusCode,
      })
    : getClinicalStatusDisplay(condition);
  const onsetDate = getOnsetDate(condition);
  const notes = getNotes(condition);

  // Determine which actions are available
  const canEdit = !isDeleting;
  const canRemove = !isDeleting;
  const hasActions = !hideActions && (canEdit || canRemove);

  return (
    <Paper p="sm" withBorder radius="md">
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Box flex={1}>
          {/* Condition name */}
          <Text fw={600} size="sm" mb={8}>
            {conditionName}
          </Text>

          {/* Fields in 2-column grid */}
          <LabeledField
            label={t('examination.conditions.onsetDate', {
              ns: 'patient-pages',
              defaultValue: 'Onset Date',
            })}
            value={formatDate(onsetDate)}
          />

          {/* Notes */}
          {notes && (
            <Box mt={4}>
              <LabeledField
                label={t('examination.conditions.notes', {
                  ns: 'patient-pages',
                  defaultValue: 'Notes',
                })}
                value={notes}
              />
            </Box>
          )}
        </Box>

        {clinicalStatusDisplay && (
          <StatusBadge
            status={clinicalStatus?.coding?.[0]?.code ?? ''}
            label={clinicalStatusDisplay}
            variant="light"
          />
        )}

        {/* Actions menu or loading indicator */}
        {isDeleting ? (
          <Loader size="sm" />
        ) : (
          hasActions && (
            <Menu shadow="md" width={160} position="bottom-end">
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" size="sm">
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                {canEdit && (
                  <Menu.Item
                    leftSection={<IconEdit size={14} />}
                    onClick={() => onAction('edit')}
                  >
                    {t('edit', {
                      ns: 'common',
                      defaultValue: 'Edit',
                    })}
                  </Menu.Item>
                )}
                {canRemove && (
                  <Menu.Item
                    leftSection={<IconTrash size={14} />}
                    onClick={() => onAction('remove')}
                    color="red"
                  >
                    {t('delete', {
                      ns: 'common',
                      defaultValue: 'Delete',
                    })}
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )
        )}
      </Group>
    </Paper>
  );
};
